import type { BaseAIAction } from '@ben/columbia/components/assistant/actions/base';
import { CloseButton } from '@ben/columbia/components/assistant/close';
import Banner from '@ben/orion/banner';
import Icon, { IconName, IconScale } from '@ben/orion/icon';
import Spinner from '@ben/orion/spinner';
import { useDisclosure } from '@mantine/hooks';
import { useEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';
import AssistantChat from './chat';
import type { ChatMessage } from './types';

export interface AssistantPanelProps {
  resetOptions: () => void;
  selectedAiAction: BaseAIAction;
  onTriggerAction: (option: BaseAIAction) => Promise<void>;
}

const AssistantPanel = ({
  resetOptions,
  selectedAiAction,
  onTriggerAction,
}: AssistantPanelProps) => {
  const [showClose, showCloseHandlers] = useDisclosure();
  const [isProcessing, isProcessingHandlers] = useDisclosure();
  const theme = useTheme();

  useEffect(() => {
    if (onTriggerAction && selectedAiAction && !isProcessing) {
      if (!selectedAiAction.requiresUserPrompt && !selectedAiAction.executed) {
        // trigger the action if no user prompt is required
        isProcessingHandlers.open();
        void onTriggerAction(selectedAiAction).then(() => {
          isProcessingHandlers.close();
        });
        return;
      }
      isProcessingHandlers.close();
    }
  }, [selectedAiAction, onTriggerAction, isProcessing, isProcessingHandlers]);

  const onUserChat = (history: ChatMessage[], userMessage?: string) => {
    selectedAiAction.chatMessages = history;
    if (userMessage) {
      if (!selectedAiAction.args) {
        selectedAiAction.args = [];
      }
      selectedAiAction.args?.push(userMessage);
    }

    isProcessingHandlers.open();
    void onTriggerAction(selectedAiAction).then(() => {
      isProcessingHandlers.close();
    });
  };

  return (
    <AssistantPanelContainer
      onMouseEnter={showCloseHandlers.open}
      onMouseLeave={showCloseHandlers.close}
    >
      {showClose && (
        <CloseButton
          onClick={resetOptions}
          onMouseEnter={showCloseHandlers.open}
        >
          <Icon
            name={IconName.Close}
            color={theme.palette.neutral[50]}
            scale={IconScale.S}
          />
        </CloseButton>
      )}
      <AssistantPanelContent>
        <StickyBanner>
          <Banner
            variant="information"
            message="Do not use sensitive or personal information such as emails in the prompt."
          />
        </StickyBanner>
        <AssistantChat
          initialChatMessages={selectedAiAction.chatMessages}
          userActionRequired={
            selectedAiAction.requiresUserPrompt && !isProcessing
          }
          onSubmitChat={onUserChat}
        />
        {isProcessing && (
          <Processing>
            <Spinner />
            Thinking...
          </Processing>
        )}
      </AssistantPanelContent>
    </AssistantPanelContainer>
  );
};

export default AssistantPanel;

const AssistantPanelContainer = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 60px;
    right: 0;
    width: 300px;
    height: 400px;
    padding: 12px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: ${theme.borderRadius.lg};
    box-shadow: 0 4px 6px ${theme.palette.black(0.1)};
    transition: opacity 0.3s ease-in-out;
  `}
`;

const AssistantPanelContent = styled.div`
  overflow: scroll;
  height: 100%;
`;

const Processing = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const StickyBanner = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #fff;
`;
